import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import RichTooltip from './RichTooltip';
import HelpIcon from '@mui/icons-material/Help';

interface ServiceBillableStatusProps {
  service: {
    billable_status: string;
    settlement_history: string[];
  };
  withLabel?: boolean;
  notBillableIsPositive?: boolean;
}

interface StatusColor {
  primary: 'primary';
  error: 'error';
}

const BillableStatus = {
  Included: 'Included in plan',
  Partial: 'Partially covered',
  Covered: 'Covered',
  NotCovered: 'Not covered',
  NotBillable: 'Not billable',
  Settled: 'Settled',
};

const positiveStatuses = [
  BillableStatus.Included,
  BillableStatus.Partial,
  BillableStatus.Covered,
  BillableStatus.Settled,
];
const negativeStatuses = [BillableStatus.NotCovered];

const RichTooltipContent = styled('div')({ maxWidth: '18vw', fontSize: '0.8125rem' });

const normalizeStatus = (status: string) => status.trim().toLowerCase();

const positiveStatusesNormalized = positiveStatuses.map(normalizeStatus);
const negativeStatusesNormalized = negativeStatuses.map(normalizeStatus);
const notBillableNormalized = normalizeStatus(BillableStatus.NotBillable);

const getStatusColor = (status: string, notBillableIsPositive: boolean): keyof StatusColor => {
  const normalizedStatus = normalizeStatus(status || '');
  if (normalizedStatus === notBillableNormalized) {
    return notBillableIsPositive ? 'primary' : 'error';
  }
  if (positiveStatusesNormalized.includes(normalizedStatus)) {
    return 'primary';
  }
  if (negativeStatusesNormalized.includes(normalizedStatus)) {
    return 'error';
  }

  return 'primary';
};

export default function ServiceBillableStatus({
  service,
  withLabel = true,
  notBillableIsPositive = false,
}: ServiceBillableStatusProps) {
  const statusColor = getStatusColor(service.billable_status, notBillableIsPositive);

  const statusReason = (service.settlement_history ?? []).map((item) => <div>{item}</div>);
  const hasStatusReason = statusReason.length > 0;

  return (
    <Stack spacing={1} direction="row">
      {withLabel && (
        <Typography fontSize="inherit" color={statusColor}>
          {service.billable_status}
        </Typography>
      )}
      {hasStatusReason && (
        <RichTooltip
          placement="top"
          content={
            <RichTooltipContent>
              <Typography fontWeight={700} fontSize="inherit">
                Explanation: {service.billable_status}
              </Typography>
              <Typography fontSize="inherit">{statusReason}</Typography>
            </RichTooltipContent>
          }
        >
          <HelpIcon fontSize="small" name="circle-question" color={statusColor} />
        </RichTooltip>
      )}
    </Stack>
  );
}
